// @owners { team: patients-team }
import { type QueryClient } from '@tanstack/react-query';

/**
 * React Query keys for next-available-date (NAD) data.
 *
 * - {@link nextAvailableDatesQueryKeys.forPrescriptions} — one list of prescription IDs, each evaluated separately
 *   (unbundled-style). Use when invalidating or scoping cache to per-prescription NAD.
 * - {@link nextAvailableDatesQueryKeys.forOrder} — one list of prescription IDs treated as a **single bundle**
 *   (one shared NAD for the group). Optional `shipmentID` for shipment/order context. Key shape does **not** match
 *   the cart thunk, which splits unbundled vs bundled lists.
 * - {@link nextAvailableDatesQueryKeys.forCheckout} — matches **`GET /v3/next_available_dates`** as called from
 *   cart/checkout: separate unbundled vs bundled ID lists, plus optional `shipmentID` when editing an existing
 *   checkout shipment. Used by `useQueryNextAvailableDates`.
 */
type QueryParams = {
  addressID?: number;
  facilityID?: number;
  prescriptionIDs: number[];
  shipmentID?: number | null;
  flag?: boolean;
};

export const nextAvailableDatesQueryKeys = {
  /** Invalidate every NAD query: `queryClient.invalidateQueries({ queryKey: nextAvailableDatesQueryKeys.all })`. */
  all: [{ $scope: 'next-available-dates' }] as const,

  /** Prefix for keys scoped to per-prescription (unbundled) NAD. Prefer `forPrescriptions` for concrete keys. */
  prescription: () => [{ ...nextAvailableDatesQueryKeys.all[0], nadType: 'prescription' }] as const,

  /** Prefix for keys scoped to bundled (single NAD for a group) NAD. Prefer `forOrder` for concrete keys. */
  order: () => [{ ...nextAvailableDatesQueryKeys.all[0], nadType: 'order' }] as const,

  /**
   * Key for NAD where **each** prescription ID is requested independently (same idea as the API `unbundled` list).
   * Does not carry a separate bundled list; use `forCheckout` when you need both unbundled and bundled params.
   */
  forPrescriptions: ({ addressID, facilityID, prescriptionIDs }: QueryParams) =>
    [
      {
        ...nextAvailableDatesQueryKeys.prescription()[0],
        addressID,
        facilityID,
        prescriptionIDs,
      },
    ] as const,

  /**
   * Key for NAD where **all** `prescriptionIDs` are bundled together (one availability calculation for the group).
   * Optional `shipmentID` ties the key to a specific shipment when working in an order/shipment context.
   * For the cart Redux thunk / `GET /v3/next_available_dates` migration, prefer `forCheckout`, which mirrors
   * unbundled + bundled query params and edit-shipment behavior.
   */
  forOrder: ({ addressID, facilityID, prescriptionIDs, shipmentID }: QueryParams) =>
    [
      {
        ...nextAvailableDatesQueryKeys.order()[0],
        addressID,
        facilityID,
        prescriptionIDs,
        shipmentID,
      },
    ] as const,

  /**
   * Partial key that matches **all checkout-scoped NAD queries** in the cache. Use with
   * `queryClient.getQueriesData({ queryKey: nextAvailableDatesQueryKeys.checkout() })` to read every cached
   * `forCheckout` entry without knowing the exact address / prescription params.
   */
  checkout: () => [{ ...nextAvailableDatesQueryKeys.all[0], nadScope: 'checkout' as const }] as const,

  /**
   * Key for **`GET /v3/next_available_dates`** as used from cart and checkout: `unbundled` prescription IDs
   * (per-RX NAD), `bundled` prescription IDs (cart treated as one group), and optional `shipmentID` when the
   * patient is editing an existing checkout shipment. ID lists are sorted so equivalent requests share a cache entry.
   */
  forCheckout: ({
    addressID,
    facilityID,
    unbundledPrescriptionIDs,
    bundledPrescriptionIDs,
    shipmentID,
  }: {
    addressID?: number;
    facilityID?: number;
    unbundledPrescriptionIDs: readonly number[];
    bundledPrescriptionIDs: readonly number[];
    shipmentID?: number | null;
  }) =>
    [
      {
        ...nextAvailableDatesQueryKeys.checkout()[0],
        addressID: addressID ?? null,
        facilityID: facilityID ?? null,
        unbundledPrescriptionIDs: [...unbundledPrescriptionIDs].sort((a, b) => a - b),
        bundledPrescriptionIDs: [...bundledPrescriptionIDs].sort((a, b) => a - b),
        shipmentID: shipmentID ?? null,
      },
    ] as const,
};

/**
 * Marks every NAD query stale so `fetchCheckoutNextAvailableDates` does not reuse a still-fresh React Query entry
 * after cart, address, shipment, or checkout context changes. Call sites are listed in `NAD_FETCH_CALL_SITES.md`
 * under **NAD React Query invalidation**.
 */
export const invalidateNextAvailableDatesQueries = (queryClient: QueryClient) => {
  return queryClient.invalidateQueries({ queryKey: nextAvailableDatesQueryKeys.all });
};
